import { compose } from './compose'
import { defaultsDeep, cloneDeep } from 'lodash'
import Cookies from 'js-cookie'
import { LANGUAGE } from '@/constants'
import { isCancelOrAbsorted } from './create'
import * as Sentry from '@sentry/react'
import { setTraceInfo, hiidoReport, adminSentryReport, udbSentryReport } from './reports'
import { takeLatest } from './extensions'
import type { AxiosRequestConfig, AxiosInstance, AxiosResponse } from 'axios'
import type { Assign } from 'utility-types'
import type { MapTakeLatest } from './extensions'
import { OneClubPathMap, replaceToOneClubUrl } from '@/utils/user'
import { getUrlParam } from '@/utils'

export * from './reports'
export * from './types'

/**
 * 因为拦截器将返回参数修正
 * 因此这里需重写部分域的调用函数,
 * 因为TS是声明式, 所以不建议通过JS来实现方法重写
 * 所以有以下重写方式
 */

/** 网关返回 data 数据格式 */
type GatwayData = {
  isSuccess: boolean
  code?: string
  message?: string
  extMap?: any
}

/** 网关返回 body 数据格式 */
export type GatwayBody<T extends Record<string, any> = any> = {
  data: Assign<GatwayData, T>
  code: string
  message: string
  bizCode?: string
  bizMessage?: string
  timestamp?: string
  sign?: string
  signType?: string
  channel?: string
  api?: string
}
type NormalBody<T extends Record<string, any> = any> = {
  data: T
  code: string
  message: string
  bizCode?: string
  bizMessage?: string
  timestamp?: string
  sign?: string
  signType?: string
  channel?: string
  api?: string
}
/** UDB返回 body 数据格式 */
type UdbBody<T extends Record<string, any> = any> = {
  data?: T
  rescode: string
  resmsg: string
  stoken?: string
}

const responseErrorCodeInterceptors = [
  (response: AxiosResponse) => {
    // trace 海度上报
    hiidoReport(response)

    /**
     * 以为 inteceptor.response.use 中 onRejection 无法
     * 在 then 抛出异常后立即执行, 因此会导致出现
     *
     * ```ts
     * ajax.then(...).catch(...)
     * // inteceptor.onRequest -> inteceptor.onInteceptor -> then -> Promise.reject -> catch
     * // 而非
     * // inteceptor.onRequest -> inteceptor.onInteceptor -> then -> Promise.reject -> inteceptor onRejection -> catch
     * // 因而导致忽略了 inteceptor onRejection
     * ```
     * @see https://github.com/axios/axios/blob/1025d1231a7747503188459dd5a6d1effdcea928/lib/adapters/xhr.js#L66
     * @see https://github.com/axios/axios/blob/1025d1231a7747503188459dd5a6d1effdcea928/lib/core/settle.js#L12
     */
    const result = (() => {
      const { data } = response
      if (typeof data === 'undefined') {
        return Promise.reject(new Error('Failed to obtain data'))
      }

      const { code, bizMessage, message } = data

      if (code === 'SUCCESS') {
        return data
      }

      if (code === 'USER_NOT_VALID' || code === 'COOKIE_EXPIRED' || code === 'A0000001') {
        interceptorsLogout()
        return Promise.reject(new Error(code))
      }

      // 没有权限
      if (code === 'LST-123677-B0002') {
        const lang = getUrlParam('lang')
        // 无权限页多语言需要设置onclub的语言
        const search = lang ? `?lang=${lang}` : ''
        window.location.replace(`/authority${search}`)
        return Promise.reject(data)
      }

      if (code === 'DATA_NOT_FOUND') {
        return {
          ...data,
          code: 'SUCCESS',
          data: null,
        }
      }

      if (code === 'COUNTRY_UNAUTHORIZED') {
        return Promise.reject(data)
      }

      return Promise.reject({
        ...data,
        message: bizMessage || message,
      })
    })()

    if (typeof result?.catch === 'function') {
      return result.catch((rejection: Error | Record<string, any>) => {
        adminSentryReport(rejection, response)
        return Promise.reject(rejection)
      })
    }

    return result
  },
  (rejection) => {
    const { data, status } = rejection?.origin?.response || {}
    const { code } = data || {}
    if (status === 422) {
      Sentry.captureException(rejection)
      if (code === 'A0000001') {
        interceptorsLogout()
      }
      return Promise.reject(rejection)
    }
    if (code === 'A0000001') {
      interceptorsLogout()
      return
    }

    if (isCancelOrAbsorted(rejection)) {
      return Promise.reject(rejection)
    }

    adminSentryReport(rejection, rejection?.response)
    return Promise.reject(rejection)
  },
]

/** 最终 http 生成器 */
const generate = compose({
  admin: {
    interceptors(inteceptor: AxiosInstance['interceptors']) {
      inteceptor.request.use(async (requestOptions: AxiosRequestConfig) => {
        const finalOptions = cloneDeep(requestOptions)
        // 接入trace
        setTraceInfo(finalOptions)
        finalOptions.headers.device = 'ONE01-H5'
        const data = finalOptions.data || {}
        const authorization = Cookies.get('authorization')
        if (authorization) finalOptions.headers.authorization = authorization
        // 在外面获取会导致获取出来的是undefined，从而使得locallanguage 是en
        // 因为用户可能改设置，所以这里改成实时读
        const selfLanguageInfo = LANGUAGE.filter((item) => item.lang === window.localStorage.i18nextLng)
        const localLang = selfLanguageInfo[0] ? selfLanguageInfo[0].languageType : 'en'
        let lastArea = window.localStorage.lastArea
        lastArea = lastArea === 'null' || !lastArea ? '' : lastArea
        // 所有crm接口sourceSystem需传ONESHIP
        const sourceSystem = finalOptions.url.indexOf('api/crm') > 0 ? 'ONESHIP' : 'ONE01'
        finalOptions.data = defaultsDeep(data, { language: localLang, sourceCountry: lastArea, sourceSystem })
        // finalOptions.data = defaultsDeep(finalOptions.data, { language: localLang, sourceCountry: lastArea })
        // const behind = Math.random().toString(16).substr(2).padStart(19, '0')
        // finalOptions.headers['x-traceid'] = Date.now() + behind.substr(0, 19)
        return finalOptions
      })

      inteceptor.response.use(...responseErrorCodeInterceptors)
    },
    extensions: {
      takeLatest: takeLatest,
    },
  },
  udb: {
    interceptors(inteceptor: AxiosInstance['interceptors']) {
      inteceptor.response.use((response: AxiosResponse) => {
        const { data } = response || {}
        const { rescode, resmsg } = data || {}

        // 成功
        if (rescode === '0') {
          return data
        }

        udbSentryReport(resmsg, response)
        return Promise.reject(data)
      })
    },
  },
  normal: {
    interceptors(inteceptor: AxiosInstance['interceptors']) {
      inteceptor.request.use(async (requestOptions: AxiosRequestConfig) => {
        const finalOptions = cloneDeep(requestOptions)
        // 接入trace
        setTraceInfo(finalOptions)
        finalOptions.headers.device = 'ONE01-H5'
        const data = finalOptions.data || {}
        const authorization = Cookies.get('authorization')
        if (authorization) finalOptions.headers.authorization = authorization
        // 在外面获取会导致获取出来的是undefined，从而使得locallanguage 是en
        // 因为用户可能改设置，所以这里改成实时读
        const selfLanguageInfo = LANGUAGE.filter((item) => item.lang === window.localStorage.i18nextLng)
        const localLang = selfLanguageInfo[0] ? selfLanguageInfo[0].languageType : 'en'
        let lastArea = window.localStorage.lastArea
        lastArea = lastArea === 'null' || !lastArea ? '' : lastArea
        finalOptions.data = defaultsDeep(data, { language: localLang, source_country: lastArea, sourceSystem: 'ONE01' })
        // finalOptions.data = defaultsDeep(finalOptions.data, { language: localLang, sourceCountry: lastArea })
        // const behind = Math.random().toString(16).substr(2).padStart(19, '0')
        // finalOptions.headers['x-traceid'] = Date.now() + behind.substr(0, 19)
        return finalOptions
      })

      inteceptor.response.use(...responseErrorCodeInterceptors)
    },
    extensions: {
      takeLatest: takeLatest,
    },
  },
  noToast: {
    interceptors(inteceptor: AxiosInstance['interceptors']) {
      inteceptor.request.use(async (requestOptions: AxiosRequestConfig) => {
        const finalOptions = cloneDeep(requestOptions)
        // 接入trace
        setTraceInfo(finalOptions)
        finalOptions.headers.device = 'ONE01-H5'
        const data = finalOptions.data || {}
        const authorization = Cookies.get('authorization')
        if (authorization) finalOptions.headers.authorization = authorization
        // 在外面获取会导致获取出来的是undefined，从而使得locallanguage 是en
        // 因为用户可能改设置，所以这里改成实时读
        const selfLanguageInfo = LANGUAGE.filter((item) => item.lang === window.localStorage.i18nextLng)
        const localLang = selfLanguageInfo[0] ? selfLanguageInfo[0].languageType : 'en'
        let lastArea = window.localStorage.lastArea
        lastArea = lastArea === 'null' || !lastArea ? '' : lastArea
        finalOptions.data = defaultsDeep(data, { language: localLang, sourceCountry: lastArea, sourceSystem: 'ONE01' })
        // finalOptions.data = defaultsDeep(finalOptions.data, { language: localLang, sourceCountry: lastArea })
        // const behind = Math.random().toString(16).substr(2).padStart(19, '0')
        // finalOptions.headers['x-traceid'] = Date.now() + behind.substr(0, 19)
        return finalOptions
      })

      inteceptor.response.use(...responseErrorCodeInterceptors)
    },
    extensions: {
      takeLatest: takeLatest,
    },
  },
})

type GenerateBody = {
  admin: GatwayBody<unknown>
  normal: NormalBody<unknown>
  udb: UdbBody<unknown>
  noToast: GatwayBody<unknown>
}

type GenerateChains = {
  admin: ['data']
  udb: ['data']
  normal: ['data']
  noToast: ['data']
}

type GenerateExtends = MapTakeLatest<GenerateBody, GenerateChains>

/** 最终 http 实例 */
// prettier-ignore
const http = generate<GenerateBody, GenerateChains, GenerateExtends>()
export default http

const interceptorsLogout = () => {
  Cookies.remove('osudb_uid')
  Cookies.remove('osudb_appid')
  Cookies.remove('osudb_subappid')
  Cookies.remove('authorization')
  // 多版本的cookie
  Cookies.remove('AQM_VERSION')
  replaceToOneClubUrl(OneClubPathMap.login, 'logout')
}
